import { useLocation } from "react-router-dom"
import { useEffect, useMemo, useState, useCallback } from "react"
import { TAGS } from "store/api"
import { useAppDispatch } from "store/index"
import { clansAPI } from "store/apis/clansAPI/clans.api"
import { ElixirType } from "store/apis/elixirsAPI/values"
import { elixirsAPI } from "store/apis/elixirsAPI/elixirs.api"
import { locationAPI } from "store/apis/locationAPI/location.api"
import { IDarkActiveElixir } from "store/apis/darkElixirsAPI/dark-elixirs.types"
import { darkElixirsEndpoints } from "store/apis/darkElixirsAPI/dark-elixirs.api"
import { seasonLeaderboardAPI } from "store/apis/seasonLeaderboardAPI/season-leaderboard.api"
import {
  useActivateDarkElixirMutation,
  useGetActivatedDarkElixirsQuery
} from "store/apis/darkElixirsAPI"
import {
  IActiveElixir,
  IActivePremiumElixir,
  IActivePremiumElixirElixir,
  IElixir
} from "store/apis/elixirsAPI/types"
import {
  getAllElixirs,
  useActivateElixirMutation,
  useBuyElixirMutation,
  useGetUsersElixirsQuery,
  useActivatePremiumElixirMutation,
  useGetActivatedPremiumElixirsQuery,
  useGetActivatedElixirsQuery,
  useGetAllElixirsQuery
} from "store/apis/elixirsAPI"
import { ServerDate } from "helpers/server-date.class"
import { calculateRemainingTime } from "helpers/calculateRemainingTime"
import { IUseElixir } from "./types"

const SUB_TYPE_ORDER = ["Lvl 1", "Lvl 2", "Lvl 3"]

interface IUseElixirParams {
  page?: number
  filters?: any
  params?: any
}

const useElixir = ({
  page = 1,
  filters,
  params
}: IUseElixirParams = {}): IUseElixir => {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()

  const { data: activeElixirs } = useGetActivatedElixirsQuery()

  const { data: availableElixirsToBuy, isLoading: isElixirListLoading } =
    useGetAllElixirsQuery(undefined, {
      skip: !pathname.includes("buy-gold")
    })

  const [timeRemaining, setTimeRemaining] = useState<Record<string, number>>({})

  const { data: elixirsInInventory } = useGetUsersElixirsQuery(
    { page, limit: 10, ...filters },
    {
      skip: !pathname.includes("inventory"),
      ...params
    }
  )
  const { data: activatedPremiumElixirs } = useGetActivatedPremiumElixirsQuery()
  const { data: activatedDarkElixirs } = useGetActivatedDarkElixirsQuery()

  const allActiveElixirs = useMemo(
    () =>
      [
        ...(activeElixirs ?? []),
        ...(activatedPremiumElixirs ?? []),
        ...(activatedDarkElixirs ?? [])
      ] as (IActiveElixir | IActivePremiumElixir | IDarkActiveElixir)[],
    [activeElixirs, activatedPremiumElixirs, activatedDarkElixirs]
  )

  const [buyElixir, { isLoading: isBuyingLoading }] = useBuyElixirMutation()

  const [activateElixir, { isLoading: isActivatingLoading }] =
    useActivateElixirMutation()
  const [activatePremiumElixir, { isLoading: isActivatingPremiumLoading }] =
    useActivatePremiumElixirMutation()
  const [activateDarkElixir, { isLoading: isActivatingDarkLoading }] =
    useActivateDarkElixirMutation()

  const availableSpeedElixirsToBuy = useMemo(() => {
    return availableElixirsToBuy
      ?.filter((elixir) => elixir.type === ElixirType.SPEED)
      ?.sort(
        (a, b) =>
          SUB_TYPE_ORDER.indexOf(a.subType) - SUB_TYPE_ORDER.indexOf(b.subType)
      )
  }, [availableElixirsToBuy])

  const availableStaminaElixirsToBuy = useMemo(() => {
    return availableElixirsToBuy
      ?.filter((elixir) => elixir.type === ElixirType.ENDURANCE)
      ?.sort(
        (a, b) =>
          SUB_TYPE_ORDER.indexOf(a.subType) - SUB_TYPE_ORDER.indexOf(b.subType)
      )
  }, [availableElixirsToBuy])

  const handleBuy = async (
    id: string,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    try {
      const result = await buyElixir(id)
      if (result.error) {
        console.error("Error buying elixir:", result.error)
        onError?.()
      } else {
        onSuccess?.()
      }
    } catch (err) {
      console.error("Unexpected error buying elixir:", err)
      onError?.()
    }
  }

  const handleActivate = async (
    id: string,
    onActivated?: () => void,
    onError?: () => void
  ) => {
    try {
      const result = await activateElixir(id)
      if (result.error) {
        onError?.()
      } else {
        onActivated?.()
      }
    } catch {
      onError?.()
    }
  }

  const handleActivatePremium = async (
    id: string,
    onActivated?: () => void,
    onError?: () => void
  ) => {
    try {
      const result = await activatePremiumElixir(id)

      if (result.error) {
        onError?.()
      } else {
        onActivated?.()
      }
    } catch {
      onError?.()
    }
  }

  const handleActivateDark = async (
    id: string,
    onActivated?: () => void,
    onError?: () => void
  ) => {
    try {
      const result = await activateDarkElixir({ id })

      if ("error" in result && result.error) {
        onError?.()
      } else {
        onActivated?.()
      }
    } catch {
      onError?.()
    }
  }

  const handleInitElixirsList = () => {
    dispatch(getAllElixirs.initiate())
  }

  const checkInventory = useCallback(
    (elixirId: string): number =>
      elixirsInInventory?.items?.find((item) => {
        return item?.elixir._id === elixirId
      })?.count || 0,
    [elixirsInInventory]
  )

  const getElixirDetails = (elixirId: string): IElixir | null => {
    return availableElixirsToBuy?.find((item) => item._id === elixirId) || null
  }

  const isAbleToUse = useCallback(
    (type?: string, subType?: string, isPremium = false): boolean => {
      if (isPremium) {
        if (!activatedPremiumElixirs) return true
        if (!activatedPremiumElixirs.length) return false

        return activatedPremiumElixirs?.some(
          (el) => el.elixir.type === type && el.elixir.level !== subType
        )
      } else {
        return !!activeElixirs?.some(
          (elixir) =>
            elixir.elixir?.type === type && elixir.elixir?.subType !== subType
        )
      }
    },
    [activatedPremiumElixirs, activeElixirs]
  )

  const calculateCountOfActiveElixirs = (elixir?: IElixir | null) => {
    const input = elixir?._id || elixir || ""

    const activeElixirInfo = allActiveElixirs.find(
      (data) => data.elixir === input || data.elixir._id === input
    )

    if (!elixir || !activeElixirInfo || !activeElixirInfo.finishedAt) {
      return 0
    }

    const start = new ServerDate(
      (activeElixirInfo as IActiveElixir)?.startedAt ??
        (activeElixirInfo as unknown as IActivePremiumElixir)?.activatedAt
    ).getTime()
    const end = new ServerDate(activeElixirInfo.finishedAt).getTime()
    const differenceMs = end - start

    const activatedElixir = activeElixirInfo.elixir as IElixir &
      IActivePremiumElixirElixir
    const elixirDurationMs =
      activatedElixir.durationHours != null
        ? activatedElixir.durationHours * 60 * 60 * 1000
        : activatedElixir.durationMs

    if (!elixirDurationMs) return 0

    return Math.ceil(differenceMs / elixirDurationMs)
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const updatedTimeRemaining: Record<string, number> = {}
      allActiveElixirs
        .filter((el) => el.finishedAt)
        .forEach((activeElixirInfo) => {
          if (activeElixirInfo.finishedAt) {
            const remainingTime = calculateRemainingTime(
              activeElixirInfo.finishedAt
            )
            updatedTimeRemaining[activeElixirInfo.elixir._id] = remainingTime

            if (!remainingTime) {
              dispatch(
                seasonLeaderboardAPI.util.invalidateTags([
                  {
                    type: TAGS.SEASON_LEADERBOARD,
                    id: `LEADERBOARD`
                  }
                ])
              )
              dispatch(
                locationAPI.util.invalidateTags([
                  { type: TAGS.USER, id: "progress" }
                ])
              )
              dispatch(
                elixirsAPI.util.invalidateTags([
                  { type: TAGS.ELIXIRS_ACTIVATED, id: "LIST" }
                ])
              )
              dispatch(
                darkElixirsEndpoints.util.invalidateTags([
                  { type: TAGS.DARK_ELIXIRS_ACTIVATED, id: "LIST" }
                ])
              )
              dispatch(
                clansAPI.util.invalidateTags([
                  {
                    type: TAGS.CLANS,
                    id: "CLANS"
                  },
                  {
                    type: TAGS.CLANS,
                    id: "CLAN_POSITION"
                  },
                  {
                    type: TAGS.CLANS,
                    id: "USER_CLAN"
                  },
                  {
                    type: TAGS.CLANS,
                    id: "MEMBERS"
                  }
                ])
              )
            }
          }
        })

      setTimeRemaining((prev) => {
        const changed =
          Object.keys(updatedTimeRemaining).some(
            (k) => prev[k] !== updatedTimeRemaining[k]
          ) || Object.keys(prev).some((k) => !(k in updatedTimeRemaining))
        return changed ? updatedTimeRemaining : prev
      })

      if (Object.values(updatedTimeRemaining).every((time) => time <= 0)) {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [allActiveElixirs])

  return {
    elixirsInInventory: [...(elixirsInInventory?.items ?? [])],
    totalElixirs: elixirsInInventory?.total || 0,
    isInventoryLoading: false,
    isInventoryError: null,
    activeElixirs: activeElixirs ?? [],
    activatedPremiumElixirs: [...(activatedPremiumElixirs ?? [])],
    activatedDarkElixirs: [...(activatedDarkElixirs ?? [])],
    availableElixirsToBuy: availableElixirsToBuy ?? [],
    availableSpeedElixirsToBuy: availableSpeedElixirsToBuy ?? [],
    availableStaminaElixirsToBuy: availableStaminaElixirsToBuy ?? [],
    isActivatingLoading,
    isActivatingPremiumLoading,
    isActivatingDarkLoading,
    isElixirListLoading: !!isElixirListLoading,
    isBuyingLoading,
    timeRemaining,
    buyElixir: handleBuy,
    activateElixir: handleActivate,
    activatePremiumElixir: handleActivatePremium,
    activateDarkElixir: handleActivateDark,
    initializeElixirsList: handleInitElixirsList,
    checkInventory,
    getElixirDetails,
    isAbleToUse,
    calculateCountOfActiveElixirs
  }
}

export default useElixir
